/**
 * Run state — survives chunk transitions, dies with the party.
 *
 * Kept deliberately mutable & global-ish because every scene/HUD piece
 * needs to read it; passing it through constructors gets noisy fast.
 */
/** Persistent party member across chunks — keyed by role so we can re-spawn
 * the correct UnitDef every chunk while preserving HP/morale/permadeath state. */
export interface RosterEntry {
  role: 'vanguard' | 'marksman' | 'medic' | 'mage';
  hp: number;
  morale: number;
  alive: boolean;
  /** Equipped item IDs by slot. Empty slot = undefined. */
  equipment: Partial<Record<string, string>>;
}

export interface RunStats {
  /** Surface progress: number of overland chunks crossed. */
  distance: number;
  /** Dungeon depth — only counts inside cave/crypt/dungeon chunks. 0 on surface. */
  depth: number;
  enemiesKilled: number;
  enemiesRouted: number;
  lootGathered: number;
  goldCarried: number;
  chunksClearedThisRun: number;
  potions: number;
  rations: number;
  hunger: number;        // 0-100, 0 = starving
  townsVisited: number;
  medicHired: boolean;
  mageHired: boolean;
  roster: RosterEntry[];  // source of truth for party between chunks
  inventory: string[];    // shared bag — item IDs
  startedAt: number;
}

export function newRun(): RunStats {
  return {
    distance: 1,
    depth: 0,
    enemiesKilled: 0,
    enemiesRouted: 0,
    lootGathered: 0,
    goldCarried: 0,
    chunksClearedThisRun: 0,
    potions: 0,
    rations: 3,
    hunger: 100,
    townsVisited: 0,
    medicHired: false,
    mageHired: false,
    roster: [
      { role: 'vanguard', hp: 110, morale: 80, alive: true, equipment: { weapon: 'iron_sword', chest: 'light_armor' } },
      { role: 'marksman', hp: 65,  morale: 70, alive: true, equipment: { weapon: 'longbow' } },
    ],
    inventory: ['dagger', 'strength_ring'],
    startedAt: performance.now(),
  };
}

export function runDurationSeconds(run: RunStats): number {
  return Math.round((performance.now() - run.startedAt) / 1000);
}

let current: RunStats = newRun();

export const Run = {
  get(): RunStats { return current; },
  reset(): RunStats { current = newRun(); return current; },
};
